import React, { useCallback, useEffect, useState } from 'react';
import { Button, Card, Drawer, Form, Input, Modal, Select, Space, Tag, Typography, message, Row, Col } from 'antd';
import { MessageOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { API_BASE as apiBase, authHeaders } from '../config/api';

const { Title, Text } = Typography;

function isAdmin() {
  try {
    const raw = localStorage.getItem('user');
    if (!raw) return false;
    const u = JSON.parse(raw);
    const roles = (u?.roles || []).map((r) => String(r).toLowerCase());
    const role = String(u?.role || '').toLowerCase();
    return roles.some((r) => r.includes('super')) || roles.includes('admin') || role.includes('super') || role === 'admin';
  } catch {
    return false;
  }
}

const priorityOptions = [
  { label: 'Low', value: 'low' },
  { label: 'Normal', value: 'normal' },
  { label: 'High', value: 'high' },
  { label: 'Urgent', value: 'urgent' },
];

const statusOptions = [
  { label: 'Open', value: 'open' },
  { label: 'In Progress', value: 'in_progress' },
  { label: 'Resolved', value: 'resolved' },
  { label: 'Closed', value: 'closed' },
];

const subjectOptions = [
  { label: 'Technical Issue', value: 'Technical Issue' },
  { label: 'Account Problem', value: 'Account Problem' },
  { label: 'Billing Question', value: 'Billing Question' },
  { label: 'Feature Request', value: 'Feature Request' },
  { label: 'Bug Report', value: 'Bug Report' },
  { label: 'Login Issue', value: 'Login Issue' },
  { label: 'Performance Issue', value: 'Performance Issue' },
  { label: 'General Inquiry', value: 'General Inquiry' },
  { label: 'Other', value: 'Other' },
];

const Support = () => {
  const admin = isAdmin();
  const [loading, setLoading] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [newTicketOpen, setNewTicketOpen] = useState(false);
  const [ticketForm] = Form.useForm();

  const [viewing, setViewing] = useState(null); // {ticket}
  const [messages, setMessages] = useState([]);
  const [msgLoading, setMsgLoading] = useState(false);
  const [msgForm] = Form.useForm();

  const loadTickets = useCallback(async () => {
    setLoading(true);
    try {
      const url = admin ? `${apiBase}/tickets` : `${apiBase}/my/tickets`;
      const res = await fetch(url, { headers: { 'Content-Type': 'application/json', ...authHeaders() } });
      if (!res.ok) throw new Error((await res.json()).error || 'Failed to load tickets');
      const data = await res.json();
      setTickets(data || []);
    } catch (e) {
      message.error(e.message || 'Error loading tickets');
    } finally {
      setLoading(false);
    }
  }, [admin]);

  useEffect(() => { loadTickets(); }, [loadTickets]);

  const openNewTicket = () => { ticketForm.resetFields(); setNewTicketOpen(true); };

  const submitTicket = async () => {
    try {
      const values = await ticketForm.validateFields();
      const res = await fetch(`${apiBase}/tickets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(values),
      });
      if (!res.ok) throw new Error((await res.json()).error || 'Failed to create ticket');
      message.success('Ticket created');
      setNewTicketOpen(false);
      loadTickets();
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e.message || 'Error creating ticket');
    }
  };

  const deleteTicket = useCallback(async (ticket_id) => {
    try {
      const res = await fetch(`${apiBase}/tickets/${ticket_id}`, { method: 'DELETE', headers: { ...authHeaders() } });
      if (!res.ok) throw new Error((await res.json()).error || 'Delete failed');
      message.success('Ticket deleted');
      loadTickets();
    } catch (e) {
      message.error(e.message || 'Error deleting ticket');
    }
  }, [loadTickets]);

  const updateTicket = useCallback(async (ticket_id, patch) => {
    try {
      const res = await fetch(`${apiBase}/tickets/${ticket_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(patch),
      });
      if (!res.ok) throw new Error((await res.json()).error || 'Update failed');
      loadTickets();
    } catch (e) {
      message.error(e.message || 'Error updating ticket');
    }
  }, [loadTickets]);

  const openTicket = useCallback(async (ticket) => {
    setViewing(ticket);
    setMessages([]);
    setMsgLoading(true);
    try {
      const res = await fetch(`${apiBase}/tickets/${ticket.ticket_id}/messages`, { headers: { ...authHeaders() } });
      if (!res.ok) throw new Error((await res.json()).error || 'Failed to load messages');
      setMessages(await res.json());
    } catch (e) {
      message.error(e.message || 'Error loading messages');
    } finally {
      setMsgLoading(false);
    }
  }, []);

  const sendMessage = async () => {
    try {
      const { message: body } = await msgForm.validateFields();
      const res = await fetch(`${apiBase}/tickets/${viewing.ticket_id}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ message: body }),
      });
      if (!res.ok) throw new Error((await res.json()).error || 'Failed to send');
      msgForm.resetFields();
      openTicket(viewing);
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e.message || 'Error sending message');
    }
  };


  return (
    <div>
      <Title level={2} style={{ marginBottom: 16 }}>Support</Title>
      {!admin && (
        <Card style={{ borderRadius: 12, marginBottom: 12 }}>
          <Space>
            <Button type="primary" onClick={openNewTicket}>New Ticket</Button>
            <Button onClick={loadTickets} disabled={loading}>Refresh</Button>
          </Space>
        </Card>
      )}
      {admin && (
        <Space style={{ marginBottom: 12 }}>
          <Button onClick={loadTickets} disabled={loading}>Refresh</Button>
        </Space>
      )}

      {/* Dashboard-style ticket cards */}
      {tickets.length > 0 ? (
        <Row gutter={[16, 16]}>
          {tickets.map(ticket => (
            <Col xs={24} sm={12} lg={8} xl={6} key={ticket.ticket_id}>
              <Card 
                hoverable 
                style={{ borderRadius: 14 }} 
                bodyStyle={{ padding: 20 }}
              >
                {/* Header with ticket info */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ 
                      width: 44, 
                      height: 44, 
                      borderRadius: 10, 
                      background: '#f59e0b22', 
                      color: '#f59e0b', 
                      display: 'grid', 
                      placeItems: 'center', 
                      fontSize: 20 
                    }}>
                      <MessageOutlined />
                    </div>
                    <div>
                      <div style={{ fontSize: 18, fontWeight: 600, color: '#1f2937' }}>#{ticket.ticket_id}</div>
                      {admin && (
                        <div style={{ color: '#64748b', fontSize: 13 }}>{ticket.email}</div>
                      )}
                    </div>
                  </div>
                  <Tag color={ticket.status === 'open' ? 'green' : ticket.status === 'resolved' ? 'blue' : ticket.status === 'closed' ? 'default' : 'orange'}>
                    {ticket.status}
                  </Tag>
                </div>

                {/* Subject */}
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#1f2937', marginBottom: 4 }}>
                    {ticket.subject}
                  </div>
                </div>

                {/* Priority indicator */}
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 12, 
                  padding: '12px 16px', 
                  background: '#f8fafc', 
                  borderRadius: 8, 
                  marginBottom: 16 
                }}>
                  <div style={{ 
                    width: 32, 
                    height: 32, 
                    borderRadius: 6, 
                    background: {low:'#64748b22',normal:'#3b82f622',high:'#f59e0b22',urgent:'#ef444422'}[ticket.priority] || '#64748b22', 
                    color: {low:'#64748b',normal:'#3b82f6',high:'#f59e0b',urgent:'#ef4444'}[ticket.priority] || '#64748b', 
                    display: 'grid', 
                    placeItems: 'center', 
                    fontSize: 16 
                  }}>
                    <ClockCircleOutlined />
                  </div>
                  <div>
                    <Text type="secondary" style={{ fontSize: 12 }}>Priority</Text>
                    <div style={{ 
                      fontSize: 14, 
                      fontWeight: 600, 
                      color: {low:'#64748b',normal:'#3b82f6',high:'#f59e0b',urgent:'#ef4444'}[ticket.priority] || '#64748b',
                      textTransform: 'capitalize'
                    }}>
                      {ticket.priority}
                    </div>
                  </div>
                </div>

                {/* Action buttons */}
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <Button 
                    size="small" 
                    onClick={() => openTicket(ticket)}
                    style={{ flex: 1, minWidth: 'auto' }}
                  >
                    View
                  </Button>
                  {admin && (
                    <>
                      <Select
                        size="small"
                        style={{ width: 100 }}
                        value={ticket.status}
                        onChange={(v) => updateTicket(ticket.ticket_id, { status: v })}
                        options={statusOptions}
                      />
                      <Select
                        size="small"
                        style={{ width: 90 }}
                        value={ticket.priority}
                        onChange={(v) => updateTicket(ticket.ticket_id, { priority: v })}
                        options={priorityOptions}
                      />
                      <Button 
                        danger 
                        size="small" 
                        onClick={() => deleteTicket(ticket.ticket_id)}
                      >
                        Delete
                      </Button>
                    </>
                  )}
                </div>
              </Card>
            </Col>
          ))}
        </Row>
      ) : (
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          <Text type="secondary">No support tickets found.</Text>
        </div>
      )}

      <Modal
        title="Create Support Ticket"
        open={newTicketOpen}
        onOk={submitTicket}
        onCancel={() => setNewTicketOpen(false)}
        okText="Create"
      >
        <Form form={ticketForm} layout="vertical">
          <Form.Item name="subject" label="Subject" rules={[{ required: true, message: 'Please select a subject' }]}>
            <Select 
              placeholder="Choose a subject for your issue"
              options={subjectOptions}
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
            />
          </Form.Item>
          <Form.Item 
            name="description" 
            label="Description" 
            rules={[{ required: true, message: 'Please provide a detailed description of your issue' }]}
          >
            <Input.TextArea 
              rows={4} 
              placeholder="Please provide detailed information about your issue, including any error messages, steps to reproduce, or other relevant details..."
            />
          </Form.Item>
          <Form.Item name="priority" label="Priority" initialValue="normal">
            <Select options={priorityOptions} />
          </Form.Item>
        </Form>
      </Modal>

      <Drawer
        title={viewing ? `Ticket #${viewing.ticket_id} — ${viewing.subject}` : 'Ticket'}
        open={!!viewing}
        onClose={() => { setViewing(null); setMessages([]); }}
        width={720}
      >
        {msgLoading ? <Text type="secondary">Loading messages...</Text> : (
          <>
            <div style={{ maxHeight: 360, overflowY: 'auto', paddingRight: 8 }}>
              {messages.length === 0 ? (
                <Text type="secondary">No messages yet</Text>
              ) : (
                messages.map((m) => (
                  <Card key={m.message_id} size="small" style={{ marginBottom: 8 }}>
                    <Space direction="vertical" size={2}>
                      <Text strong>{m.sender_email || `User #${m.sender_id}`}</Text>
                      <Text>{m.message}</Text>
                      <Text type="secondary" style={{ fontSize: 12 }}>{new Date(m.created_at).toLocaleString()}</Text>
                    </Space>
                  </Card>
                ))
              )}
            </div>
            <Form form={msgForm} layout="vertical" style={{ marginTop: 12 }}>
              <Form.Item name="message" label="Reply" rules={[{ required: true, message: 'Enter a message' }]}>
                <Input.TextArea rows={3} placeholder="Type your message..." />
              </Form.Item>
              <Button type="primary" onClick={sendMessage}>Send</Button>
            </Form>
          </>
        )}
      </Drawer>
    </div>
  );
};

export default Support;
